import vid from "../../assets/video/cvmarket-homepage-scroll.mp4.asset.json";
import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/cvmarket";

const img = (
  file: string,
  alt: string,
  span: 2 | 3 | 6 = 3,
  width = 1920,
  height = 1246,
) => ({
  src: `${dir}/${file}.webp`,
  src900: `${dir}/${file}-900.webp`,
  alt,
  width,
  height,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string, span: 2 | 3 = 3) => ({
  src: `${dir}/${file}.webp`,
  src900: `${dir}/${file}-900.webp`,
  alt,
  width: 1920,
  height: 1600,
  span,
});

export const cvmarket: CaseStudy = {
  slug: "cvmarket",
  title: "Цветочный маркет",
  cardTitle: "Разработка интернет-магазина цветов «Цветочный маркет»",
  subtitle: "Интернет-магазин цветов с доставкой",
  industry: { label: "Интернет-магазины", slug: "ecommerce" },
  eyebrow: "Интернет-магазин · Цветы и доставка",
  h1: "Разработка интернет-магазина цветов «Цветочный маркет»",
  lead:
    "Собрали интернет-магазин цветов с крупным каталогом, понятной карточкой букета и быстрым мобильным оформлением заказа, а заказы связали с RetailCRM — от клика в каталоге до сборки букета в магазине.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить интернет-магазин" },

  seo: {
    primaryKeyword: "разработка интернет-магазина цветов",
    secondaryKeywords: [
      "создание интернет-магазина цветов",
      "разработка сайта доставки цветов",
      "интернет-магазин букетов под ключ",
      "дизайн интернет-магазина цветов",
      "каталог и карточка товара для цветочного магазина",
      "интеграция интернет-магазина с RetailCRM",
      "мобильный интернет-магазин цветов",
    ],
    semanticEntities: [
      "интернет-магазин цветов",
      "каталог букетов",
      "карточка товара",
      "фильтры каталога",
      "корзина",
      "оформление заказа",
      "доставка и самовывоз",
      "RetailCRM",
      "мобильная версия",
      "сеть магазинов",
    ],
    excludedKeywords: [
      "заказать букет",
      "купить цветы недорого",
      "доставка роз",
      "цветы с доставкой по Москве",
    ],
    title: "Разработка интернет-магазина цветов — кейс «Цветочный маркет»",
    description:
      "Кейс HAY.agency: дизайн и разработка интернет-магазина цветов «Цветочный маркет» — каталог, карточка букета, мобильный заказ и связка с RetailCRM.",
    h1: "Разработка интернет-магазина цветов «Цветочный маркет»",
    slug: "cvmarket",
    canonicalUrl: `${SITE_URL}/cases/cvmarket`,
    ogTitle: "Разработка интернет-магазина цветов «Цветочный маркет»",
    ogDescription:
      "Каталог, карточка букета, мобильное оформление заказа и связка сайта с RetailCRM для цветочной розницы.",
    ogImage: `${SITE_URL}${dir}/cvmarket-og.webp`,
    robotsIndex: true,
    datePublished: "2023-09-01",
    dateModified: "2026-08-29",
  },

  links: {
    relatedService: {
      label: "Разработка и развитие интернет-магазинов",
      to: routes.ecom,
      note: "Каталог, карточка товара и сценарии покупки",
    },
    relatedIndustry: {
      label: "Внедрение и поддержка RetailCRM",
      to: routes.retailcrm,
      note: "Обработка заказов интернет-магазина и работа с клиентом",
    },
    relatedCases: [
      {
        label: "Комплексное продвижение сети «Городская База Цветов»",
        to: "/cases/gbcvet",
        note: "Сайт, RetailCRM, SEO и геоперформанс для цветочной сети",
      },
      {
        label: "Сайт-каталог для производителя заборов Artline",
        to: "/cases/artline",
        note: "Крупный товарный каталог и заявки из поиска",
      },
      {
        label: "Комплексное продвижение строительной компании ESG",
        to: "/cases/esg",
        note: "Сайт, SEO и Директ для нескольких направлений бизнеса",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен интернет-магазин, который удобно покупать с телефона?",
    text:
      "Спроектируем каталог, карточку товара и оформление заказа, свяжем сайт с CRM и возьмём проект на сопровождение.",
    button: "Обсудить интернет-магазин",
  },

  client: {
    name: "Цветочный маркет",
    url: "https://cvmarket.ru/",
    city: "Москва",
    industry: "Цветочная розница и доставка",
  },
  year: "С 2023 года",
  services: [
    "Дизайн интернет-магазина",
    "Разработка каталога",
    "Карточка товара",
    "Мобильная версия",
    "Интеграция с RetailCRM",
    "Сопровождение проекта",
  ],
  platform: "1С-Битрикс + RetailCRM",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "RetailCRM", to: routes.retailcrm },
    { label: "Каталог", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "Цветочный маркет" },
    { label: "Отрасль", value: "Цветочная розница и доставка" },
    { label: "Тип проекта", value: "Интернет-магазин с доставкой и самовывозом" },
    { label: "Платформа", value: "1С-Битрикс + RetailCRM" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Дизайн интернет-магазина цветов и единая визуальная система каталога",
      "Каталог с разделами, фильтрами по цене, цвету, количеству и сортировкой",
      "Карточка букета: размеры, цена, бонусы, доставка и самовывоз",
      "Корзина и быстрое оформление заказа в мобильной версии",
      "Связка заказов сайта с RetailCRM и работой магазинов",
      "Разделы доставки, оплаты и сети магазинов",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Цветочная розница — импульсный спрос: покупатель выбирает букет за несколько минут, чаще всего с телефона, и почти всегда сравнивает цену, размер и срок доставки. Магазину нужен был интернет-магазин, который выдерживает большой ассортимент и при этом остаётся простым в покупке.",
        "Отдельная задача — не оставить заказы «в сайте»: они должны сразу попадать в систему обработки, где их видят операторы и флористы.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн интернет-магазина: товар на первом плане",
    paragraphs: [
      "Основа интерфейса — фотография букета. Интерфейс намеренно светлый и нейтральный, акцентный красный используется только для цены, скидки и целевых действий: «В корзину» и «Перейти к оформлению».",
      "Главная страница ведёт покупателя в каталог через понятные разделы — Premium-букеты, розы, монобукеты, поводы и получателей, — а карточки товара показывают цену, скидку и размер до перехода.",
    ],
    bullets: [
      "Крупная товарная сетка и одинаковые карточки в каталоге",
      "Акцентный цвет — только для цены и кнопок действия",
      "Понятные разделы вместо длинного меню",
    ],
  },

  identity: {
    fonts: [
      { name: "Гротеск Bold", role: "Заголовки и цены", sample: "Букеты и монобукеты" },
      { name: "Гротеск Regular", role: "Основной текст интерфейса", sample: "Доставка и самовывоз" },
    ],
    colors: [
      { hex: "#E4123F", name: "Акцент действия" },
      { hex: "#FDE4E8", name: "Светлый фон" },
      { hex: "#F5A623", name: "Кнопка покупки" },
      { hex: "#1A1A1A", name: "Текст" },
    ],
  },

  cover: {
    src: `${dir}/cvmarket-case-cover.webp`,
    src900: `${dir}/cvmarket-case-cover-900.webp`,
    alt: "Интернет-магазин цветов «Цветочный маркет»: главная страница, каталог и мобильное оформление заказа",
    width: 1920,
    height: 1080,
    wide: false,
    span: 6,
  },

  coverVideo: vid.url,
  coverPoster: {
    src: `${dir}/cvmarket-homepage-scroll-poster.webp`,
    src900: `${dir}/cvmarket-homepage-scroll-poster-900.webp`,
    alt: "Прокрутка главной страницы интернет-магазина цветов «Цветочный маркет»",
    width: 1920,
    height: 1080,
    span: 6,
    wide: true,
  },

  gallery: [
    img(
      "cvmarket-home-categories",
      "Разделы каталога на главной: Premium-букеты, розы, монобукеты, поводы",
      3,
    ),
    img(
      "cvmarket-catalog-page",
      "Каталог интернет-магазина цветов с товарной сеткой и ценами",
      3,
    ),
  ],

  responsive: {
    text: [
      "Большая часть заказов оформляется с телефона, поэтому мобильные сценарии проектировали первыми: поиск букета, карточка товара, корзина и переход к оформлению.",
      "В мобильной версии видны цена, скидка, размер букета и способ получения заказа — покупателю не нужно возвращаться назад, чтобы принять решение.",
    ],
    images: [
      mobile("cvmarket-mobile-discovery", "Мобильный поиск и каталог букетов с фильтрами и ценами", 3),
      mobile("cvmarket-mobile-purchase", "Мобильная корзина и оформление заказа", 3),
    ],
  },

  columnBlocks: [
    {
      id: "catalog",
      title: "Каталог и карточка товара",
      paragraphs: [
        "Каталог рассчитан на большой ассортимент: разделы, фильтры по цене, цвету, количеству цветов и сорту, сортировка и быстрый переход к товару. Карточка букета показывает фотографию, размеры, цену со скидкой, бонусы и способ получения заказа.",
      ],
      images: [
        img("cvmarket-catalog-filters", "Фильтры каталога: цена, цвет, количество цветов, сорт", 3),
        img("cvmarket-product-card", "Карточка букета: цена, размер, бонусы и кнопка «В корзину»", 3),
      ],
      imageCols: 2,
      caption: "Фильтры и карточка товара помогают выбрать букет без лишних переходов.",
    },
    {
      id: "retailcrm",
      title: "Сайт и RetailCRM работают как одна система",
      paragraphs: [
        "Заказы с сайта попадают в RetailCRM: там их видят операторы, распределяют по магазинам и передают флористам. Клиентская история, статусы заказа и коммуникации хранятся в одном месте.",
      ],
      columns: [
        {
          title: "Маршрут заказа",
          items: ["Каталог", "Карточка букета", "Корзина", "RetailCRM", "Магазин и флорист", "Доставка или самовывоз"],
        },
        {
          title: "Что связали",
          items: ["Передачу заказов", "Карточку клиента", "Статусы заказа", "Способы получения"],
        },
        {
          title: "Сопровождение",
          items: ["Развитие каталога", "Доработки интерфейса", "Техническая поддержка"],
        },
      ],
      images: [
        img("cvmarket-basket-filled", "Корзина с добавленным букетом", 3),
        img("cvmarket-checkout-filled", "Оформление заказа: способ получения и оплата", 3),
      ],
      imageCols: 2,
    },
    {
      id: "delivery-network",
      title: "Доставка, оплата и сеть магазинов",
      paragraphs: [
        "Отдельные разделы сайта закрывают вопросы, которые покупатель задаёт до заказа: сроки и стоимость доставки, способы оплаты, адреса магазинов и точки самовывоза.",
      ],
      images: [
        img("cvmarket-delivery-payment", "Условия доставки и способы оплаты в интернет-магазине цветов", 2),
        img("cvmarket-stores-map", "Карта сети магазинов «Цветочный маркет»", 2),
        img("cvmarket-stores-list", "Список работающих магазинов сети с адресами", 2),
      ],
      imageCols: 3,
    },
    {
      id: "extra",
      title: "Избранное и акции",
      paragraphs: [
        "Покупатель может сохранять понравившиеся букеты в избранное и возвращаться к ним позже, а раздел акций собирает товары со скидками и сезонные предложения.",
      ],
      images: [
        img("cvmarket-favorites-filled", "Раздел избранного с сохранёнными букетами", 3),
        img("cvmarket-promotions", "Раздел акций и скидок на букеты", 3),
      ],
      imageCols: 2,
    },
  ],

  servicesDone: {
    title: "Что сделали в проекте",
    items: [
      {
        label: "Разработка интернет-магазина",
        to: routes.ecom,
        note: "Дизайн, каталог, карточка товара и оформление заказа",
      },
      {
        label: "Интеграция с RetailCRM",
        to: routes.retailcrm,
        note: "Передача заказов, статусы и работа с клиентом",
      },
      {
        label: "Сопровождение проекта",
        to: routes.uslugi,
        note: "Развитие каталога, доработки и техническая поддержка",
      },
    ],
  },
};
